import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import loadBrandCategories from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrandCategories";
import loadBrandByPath from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrandByPath";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import { getProp } from "@insite/mobius/utilities";
import CENDropDownIcon from "@tCentro/Icons/CENDropDownIcon";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import NISCustomLoadBrandData from "@tCentro/Handlers/NISCustomLoadBrandData";
import nisLoadAllBrands from "@tCentro/Handlers/NISLoadAllBrandsExtention";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { useDispatch } from "react-redux";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => ({
    brandId: state.t_components.CustomBrandData.brandId,
    homepageFilterData: state.t_components.CustomHomepageBrandFiltersData,
});

const mapDispatchToProps = {
    nisLoadAllBrands,
    loadAllBrands,
    loadBrandCategories,
    loadBrandByPath,
    NISCustomLoadBrandData,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NISBrandFilterListStyles {
    skipNav?: SkipNavStyles;
    titleText?: TypographyPresentationProps;
    tag?: TagPresentationProps;
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    filterTag?: TagPresentationProps;
    filterWrapper?: InjectableCss;
    filterLink?: InjectableCss;
    allbrandsLink?: LinkPresentationProps;
    filterAllBrandsLink?: InjectableCss;
    acitveBrandLink?: InjectableCss;
    topicsSelect?: SelectPresentationProps;
}

export const brandFilterListStyles: NISBrandFilterListStyles = {
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        padding-left: 16px;
                    `,
                    css`
                        padding-left: 64px;
                    `,
                    css`
                        padding-left: 64px;
                    `,
                ])}
        `,
    },
    filterWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin: 20px 0 20px;
            flex-direction: column;
            background: #f0f0f0;
            border-radius: 16px;
            padding: 20px 0;

            @media (min-width: 1199px) {
                margin: 20px 24px 20px 0px;
            }
            @media (max-width: 1198px) {
                margin: 20px 16px 20px 0px;
            }
        `,
    },
    filterLink: {
        css: css`
            padding: 3px 32px;
            cursor: pointer;
            border-bottom: 1px solid #e0dfe0;
            font: 400 18px/28px Roboto;
            transition: all 0.3s ease;
            position: relative;
            outline: none;

            & > span,
            & > a,
            & > a > span {
                color: #636161 !important;
                text-decoration: none;
            }

            &:hover {
                background-color: #fff;
                font-family: Arial;
                font-weight: 700;
                transition: all 0.3s ease;

                &:after {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 4px;
                    height: 100%;
                    background: #fbd51e;
                }
            }
        `,
    },
    allbrandsLink: {
        css: css`
            background: #2b5197;
            border-radius: 4px;
            font: 700 16px/16px "Roboto Condensed";
            text-transform: uppercase;
            width: 226px;
            height: 40px;
            justify-content: center;
            margin: 16px auto 0;
            padding: 8px 16px;
            gap: 8px;

            & > span {
                color: #fff !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                        null,
                        css`
                            width: 171px;
                        `,
                        null,
                    ],
                    "min",
                )}
            @media (min-width: 1199px) {
                width: 226px;
            }
        `,
    },
    filterAllBrandsLink: {
        css: css`
            padding: 15px 30px;
            cursor: pointer;
            border-bottom: 1px solid #e0dfe0;
            background-color: #fff;
            font: 700 18px/28px Arial;
            transition: all 0.3s ease;
            position: relative;

            &:after {
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 4px;
                height: 100%;
                background: #2b5197;
            }
        `,
    },
    acitveBrandLink: {
        css: css`
            padding: 3px 32px;
            cursor: pointer;
            border-bottom: 1px solid #e0dfe0;
            background-color: #fff;
            font: 700 18px/28px Arial;
            transition: all 0.3s ease;
            position: relative;

            &:after {
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 4px;
                height: 100%;
                background: #2b5197;
            }
        `,
    },
    topicsSelect: {
        iconProps: {
            src: CENDropDownIcon,
            css: css`
                width: 28px !important;
                height: 28px !important;
                top: 10px !important;
                right: 24px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                padding-top: 24px;

                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;

                select {
                    background: ${COLORS.background.grayShade} !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;
                }

                span[class^="IconWrapper"] {
                    background: url("/userFiles/images/chevronDown.svg") no-repeat center;
                    svg {
                        display: none;
                    }
                }
            `,
        },
    },
};

const styles = brandFilterListStyles;

const NISBrandFilterList: FC<Props> = ({
    nisLoadAllBrands,
    NISCustomLoadBrandData,
    brandId,
    homepageFilterData,
    history,
}) => {
    const { homepageBrandFiltersData: brands } = homepageFilterData;
    const [isScreenWide, setIsScreenWide] = useState<boolean | null>(null);
    const lastResponseBrandId = useRef<string | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const handleResize = () => setIsScreenWide(window.innerWidth > 767);

        nisLoadAllBrands({
            sort: "name asc",
            select: "id,detailPagePath,name,logoSmallImagePath,logoAltText",
            expand: "categories",
        });

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => {
            dispatch({ type: "Custom/Brand/setInitialValue" });
            window.removeEventListener("resize", handleResize);
            lastResponseBrandId.current = null;
        };
    }, [nisLoadAllBrands]);

    useEffect(() => {
        NISCustomLoadBrandData({ brandId: "all_brands" });
        lastResponseBrandId.current = "all_brands";
    }, []);

    const onClickBrandSection = useCallback(
        (id?: string) => {
            if (id && lastResponseBrandId.current !== id) {
                lastResponseBrandId.current = id;
                NISCustomLoadBrandData({ brandId: id });
            }
        },
        [NISCustomLoadBrandData],
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const value = event.target.value;

            if (value === "") {
                history.push("/brands");
                if (lastResponseBrandId.current) {
                    lastResponseBrandId.current = null;
                }
                return;
            }

            if (lastResponseBrandId.current !== value) {
                lastResponseBrandId.current = value;
                NISCustomLoadBrandData({ brandId: value });
            }
        },
        [history, NISCustomLoadBrandData],
    );

    return (
        <StyledWrapper {...styles.wrapper} className="brandFilterList">
            {isScreenWide ? (
                <StyledWrapper {...styles.filterWrapper}>
                    <StyledWrapper
                        key={`brandFilter_all_brands`}
                        {...(brandId === "all_brands" ? styles.acitveBrandLink : styles.filterLink)}
                        onClick={() => onClickBrandSection("all_brands")}
                    >
                        {translate("All Brands")}
                    </StyledWrapper>
                    {brands?.map(item => (
                        <StyledWrapper
                            key={`brandFilter_${item.id}`}
                            {...(brandId === item.id ? styles.acitveBrandLink : styles.filterLink)}
                            onClick={() => onClickBrandSection(item.id)}
                        >
                            {item.name}
                        </StyledWrapper>
                    ))}
                    <Link {...styles.allbrandsLink} href="/brands">
                        {translate("See All Brands")}
                    </Link>
                </StyledWrapper>
            ) : (
                <Select
                    {...styles.topicsSelect}
                    value={brandId || "all_brands"}
                    onChange={handleChange}
                    onBlur={handleChange}
                >
                    <option value="all_brands">{translate("All Brands")}</option>
                    {brands?.map(item => (
                        <option key={`brandFilter_${item.id}`} value={item.id}>
                            {item.name}
                        </option>
                    ))}
                </Select>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NISBrandFilterList)),
    definition: {
        group: "Basic",
        displayName: "Brand Filter List",
    },
};

export default widgetModule;
